<template>
  <div class="density-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ρ = m / V$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="data-crystal">
               <label>量筒读数 V</label>
               <span>{{ cylinderVolume.toFixed(0) }} cm³</span>
            </div>
            <div class="data-crystal">
               <label>电子秤所测质量 m</label>
               <span>{{ scaleMass.toFixed(1) }} g</span>
            </div>
         </div>
         
         <div class="calc-box">
            <div class="label">{{ isHollowMode ? '物体的平均密度' : '物质的真实密度' }}</div>
            <div class="val">{{ (scaleMass / (cylinderVolume - 50)).toFixed(2) }} g/cm³</div>
            <div v-if="isHollowMode" class="sub-val" style="font-size: 10px; color: #94a3b8; margin-top: 4px;">材质密度: {{ targetMaterial.density }} g/cm³</div>
         </div>

         <!-- 图像模式叠加层 -->
         <div v-if="showGraph" class="graph-overlay glass">
            <svg width="100%" height="150" viewBox="0 0 300 150">
               <line x1="40" y1="120" x2="260" y2="120" stroke="#94a3b8" />
               <line x1="40" y1="120" x2="40" y2="20" stroke="#94a3b8" />
               <text x="260" y="135" fill="#94a3b8" font-size="10">体积 V</text>
               <text x="10" y="25" fill="#94a3b8" font-size="10">质量 m</text>
               <!-- 密度斜率线 -->
               <line x1="40" y1="120" :x2="40 + 200" :y2="120 - 200 * (targetMaterial.density / 20)" :stroke="targetColor" stroke-width="2" stroke-dasharray="4,2" />
               <!-- 当前采样点 -->
               <circle :cx="40 + (cylinderVolume-50)*10" :cy="120 - (scaleMass/10)" r="5" :fill="targetColor">
                  <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
               </circle>
            </svg>
            <div class="graph-tip">m-V 图像斜率即为密度 ρ</div>
         </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="choice-tabs glass">
            <label>实验控制</label>
            <div class="tabs">
               <div class="tab" :class="{ active: !isHollowMode }" @click="isHollowMode = false">实心测量</div>
               <div class="tab" :class="{ active: isHollowMode }" @click="isHollowMode = true">空心/平均密度</div>
               <div class="tab" :class="{ active: showGraph }" @click="showGraph = !showGraph">显示 m-V 图像</div>
            </div>
            <div class="tabs mt-xs">
               <div v-for="m in materials" :key="m.name" class="tab" :class="{ active: material === m.name }" @click="material = m.name">
                  {{ m.name }}
               </div>
            </div>
         </div>

         <div class="control-card glass" v-if="isHollowMode">
            <label>空心部分体积占比 ({{ hollowRatio.toFixed(0) }}%)</label>
            <el-slider v-model="hollowRatio" :min="10" :max="90" size="mini"></el-slider>
         </div>

         <div class="guide-card">
            <div class="step">
               <span class="num">1</span>
               <p v-if="!isHollowMode">测出实心金属块的 <strong>m</strong> 和 <strong>V</strong>，得出材质密度 <strong>ρ</strong>。</p>
               <p v-else>对于空心物体，所测 <strong>m/V</strong> 为平均密度，<strong>小于</strong> 材质真实密度。</p>
            </div>
            <div class="step">
               <span class="num">2</span>
               <p>通过“排水法”观察量筒液面上升值，得到体积 <strong>V = V₂ - V₁</strong>。</p>
            </div>
         </div>
         
         <div class="action-row">
            <el-button type="primary" size="small" @click="resetScene" icon="el-icon-refresh-left">重新开始实验</el-button>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'DensityMeasurementViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      material: '金',
      materials: [
        { name: '金', density: 19.3, color: 0xffd700 },
        { name: '铁', density: 7.8, color: 0xb0b0b0 },
        { name: '铝', density: 2.7, color: 0xe5e4e2 }
      ],
      isHollowMode: false,
      hollowRatio: 50, // %
      isInWater: false,
      showGraph: false,
      running: true,
      scene: null, renderer: null, camera: null, sample: null, waterPos: null
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '密度与测量核心仿真';
    },
    targetMaterial() { return this.materials.find(m => m.name === this.material); },
    targetColor() { 
      const color = this.targetMaterial.color;
      return '#' + color.toString(16).padStart(6, '0');
    },
    scaleMass() { 
       const totalVol = 10;
       const metalVol = this.isHollowMode ? totalVol * (1 - this.hollowRatio/100) : totalVol;
       return this.targetMaterial.density * metalVol; 
    },
    cylinderVolume() { return 50 + (this.isInWater ? 10 : 0); }
  },
  mounted() {
    this.initThree();
    this.detectMode();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      const cont = this.$refs.canvasContainer;
      if (cont && cont.contains(this.renderer.domElement)) cont.removeChild(this.renderer.domElement);
    }
  },
  watch: {
    material() { this.updateMaterial(); },
    isHollowMode() { this.updateMaterial(); },
    knowledgePoint() { this.detectMode(); }
  },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       if (kp.includes('空心') || kp.includes('平均密度') || kp.includes('误差')) {
          this.isHollowMode = true;
       }
       if (kp.includes('图像')) {
          this.showGraph = true;
       }
    },
    initThree() {
      const cont = this.$refs.canvasContainer;
      if (!cont) return;
      const width = cont.clientWidth;
      const height = cont.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(40, 50, 80);
      this.camera.lookAt(0, 20, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      cont.appendChild(this.renderer.domElement);

      const cylGeo = new THREE.CylinderGeometry(8, 8, 50, 32, 1, true);
      const cylMat = new THREE.MeshPhongMaterial({ color: 0xffffff, transparent: true, opacity: 0.2, side: THREE.DoubleSide });
      const cylinder = new THREE.Mesh(cylGeo, cylMat);
      cylinder.position.set(-15, 25, 0);
      this.scene.add(cylinder);

      this.waterPos = new THREE.Mesh(new THREE.CylinderGeometry(7.8, 7.8, 25, 32), new THREE.MeshPhongMaterial({ color: 0x00d2ff, transparent: true, opacity: 0.5 }));
      this.waterPos.position.set(-15, 12.5, 0);
      this.scene.add(this.waterPos);

      this.sample = new THREE.Mesh(new THREE.SphereGeometry(6, 32, 16), new THREE.MeshPhongMaterial({ color: this.targetMaterial.color, transparent: true, opacity: 1 }));
      this.sample.position.set(20, 6, 0);
      this.scene.add(this.sample);

      const scaleBase = new THREE.Mesh(new THREE.BoxGeometry(20, 2, 20), new THREE.MeshPhongMaterial({ color: 0x333 }));
      scaleBase.position.set(20, 0, 0);
      this.scene.add(scaleBase);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      const light = new THREE.DirectionalLight(0xffffff, 0.8);
      light.position.set(20, 60, 20);
      this.scene.add(light);

      this.animate();
    },
    updateMaterial() {
      if (this.sample) {
          this.sample.material.color.set(this.targetMaterial.color);
          this.sample.material.opacity = this.isHollowMode ? 0.7 : 1;
      }
      this.resetScene();
    },
    resetScene() {
      this.isInWater = false;
      if (this.sample) this.sample.position.set(20, 6, 0);
      if (this.waterPos) {
          this.waterPos.scale.y = 1;
          this.waterPos.position.y = 12.5;
      }
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      if (!this.isInWater) {
          this.sample.position.x -= 0.3;
          if (this.sample.position.x < -15) {
              this.sample.position.x = -15;
              this.sample.position.y -= 0.5;
              if (this.sample.position.y < 6) {
                  this.sample.position.y = 6;
                  this.isInWater = true;
              }
          }
      } else {
          if (this.waterPos && this.waterPos.scale.y < 1.25) {
              this.waterPos.scale.y += 0.01;
              this.waterPos.position.y += 0.125;
          }
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.density-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 10px; height: 100%; color: #fff; }
.viz-header { display: flex; justify-content: space-between; align-items: center; .brief { font-size: 14px; color: #fff; } .formula-chip { background: #0ea5e9; color: #fff; padding: 2px 10px; border-radius: 4px; font-weight: bold; } }
.main-layout { flex: 1; display: flex; flex-direction: column; min-height: 0; }
.canvas-3d-wrapper { flex: 1; background: #000; border-radius: 12px; position: relative; overflow: hidden; min-height: 200px; }
.hud-top { position: absolute; top: 15px; left: 15px; display: flex; flex-direction: column; gap: 10px; z-index: 5; }
.data-crystal { background: rgba(30, 41, 59, 0.7); border-left: 3px solid #0ea5e9; padding: 4px 12px; border-radius: 6px; label { display: block; font-size: 10px; color: #94a3b8; } span { font-size: 13px; color: #fff; font-family: monospace; font-weight: bold; } }
.calc-box { position: absolute; bottom: 20px; left: 20px; background: rgba(14, 165, 233, 0.1); border: 1px solid #0ea5e9; padding: 10px 15px; border-radius: 10px; backdrop-filter: blur(5px); z-index: 5; .label { font-size: 11px; color: #0ea5e9; text-transform: uppercase; } .val { font-size: 1.2rem; font-weight: bold; } }

.graph-overlay {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(14, 165, 233, 0.4);
  padding: 15px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  z-index: 10;
  .graph-tip { text-align: center; color: #0ea5e9; font-size: 11px; margin-top: 5px; font-weight: bold; }
}

.bottom-controls { padding: 10px 0; overflow-y: auto; }
.choice-tabs { background: #fff; color: #000; padding: 12px; border-radius: 10px; margin-bottom: 10px; label { color: #64748b; font-size: 11px; display: block; margin-bottom: 8px; } }
.tabs { display: flex; gap: 8px; flex-wrap: wrap; .tab { flex: 1; min-width: 60px; text-align: center; padding: 6px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 12px; color: #64748b; cursor: pointer; transition: all 0.2s; &.active { background: #0ea5e9; color: #fff; border-color: #0ea5e9; font-weight: bold; } } }
.mt-xs { margin-top: 8px; }
.control-card { background: #fff; color: #000; padding: 10px 15px; border-radius: 10px; margin-bottom: 10px; label { font-size: 11px; color: #64748b; display: block; margin-bottom: 5px; } }
.guide-card { background: #f0f9ff; color: #0369a1; padding: 12px; border-radius: 10px; margin-bottom: 10px; .step { display: flex; gap: 10px; margin-bottom: 8px; .num { background: #0ea5e9; color: #fff; width: 18px; height: 18px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; } p { margin: 0; font-size: 12px; line-height: 1.4; } } }
.action-row { display: flex; justify-content: center; }
</style>
